import React from 'react'
import {
  Box,
  Divider,
  FormControlLabel,
  FormControlLabelProps,
  Radio,
  RadioGroup,
  Slider,
  Typography,
} from '@mui/material'
import { useTranslation } from 'react-i18next'

import {
  exportOptions,
  pretestOptions,
  skywardenFusionStrategyOptions,
  skywardenSearchModeOptions,
} from '../main-types'
import {
  featureFlagLabels,
  filenameConventionOptions,
  FilenameConventionRadioGroupProps,
  SkywardenSectionProps,
} from './use-feature-flags-props'

type GetCheckboxProps = (label: string, field: string) => FormControlLabelProps

const FusionWeightSlider = ({
  value,
  onChangeCommitted,
  disabled,
}: {
  value: number
  onChangeCommitted: (value: number) => void
  disabled?: boolean
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.feature_flags' })
  const [localValue, setLocalValue] = React.useState(value)

  React.useEffect(() => {
    setLocalValue(value)
  }, [value])

  return (
    <Box sx={{ mt: 1, px: 1 }}>
      <Typography variant="subtitle2">{t('fusion_weight')}</Typography>
      <Slider
        value={localValue}
        onChange={(_, newValue) => setLocalValue(newValue as number)}
        onChangeCommitted={(_, newValue) => onChangeCommitted(newValue as number)}
        min={0}
        max={1}
        step={0.1}
        marks
        valueLabelDisplay="auto"
        disabled={disabled}
        size="small"
        color="secondary"
      />
      <Typography variant="caption" color="text.secondary">
        {t('fusion_weight_helper')}
      </Typography>
    </Box>
  )
}

const SkywardenSection = ({
  getSkywardenSectionProps,
}: {
  getSkywardenSectionProps: () => SkywardenSectionProps
}) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.feature_flags' })
  const { searchModeProps, fusionStrategyProps, weightInputProps } = getSkywardenSectionProps()

  const showFusionStrategy = searchModeProps.value === 'hybrid'
  const showFusionWeight =
    showFusionStrategy &&
    (fusionStrategyProps.value === 'weighted' || fusionStrategyProps.value === 'boost')

  return (
    <>
      <Divider sx={{ mt: 2 }}>{t('skywarden_search_options')}</Divider>
      <Typography variant="subtitle2" sx={{ mt: 1 }}>
        {t('search_mode')}
      </Typography>
      <RadioGroup row value={searchModeProps.value} onChange={searchModeProps.onChange}>
        {skywardenSearchModeOptions.map((option) => (
          <FormControlLabel
            key={option}
            value={option}
            control={<Radio size="small" color="secondary" />}
            disabled={searchModeProps.disabled}
            slotProps={{ typography: { fontSize: 14 } }}
            label={featureFlagLabels[option]}
          />
        ))}
      </RadioGroup>

      {showFusionStrategy && (
        <>
          <Typography variant="subtitle2" sx={{ mt: 1 }}>
            {t('fusion_strategy')}
          </Typography>
          <RadioGroup row value={fusionStrategyProps.value} onChange={fusionStrategyProps.onChange}>
            {skywardenFusionStrategyOptions.map((option) => (
              <FormControlLabel
                key={option}
                value={option}
                control={<Radio size="small" color="secondary" />}
                disabled={fusionStrategyProps.disabled}
                slotProps={{ typography: { fontSize: 14 } }}
                label={featureFlagLabels[option]}
              />
            ))}
          </RadioGroup>
        </>
      )}

      {showFusionWeight && (
        <FusionWeightSlider
          value={weightInputProps.value}
          onChangeCommitted={weightInputProps.onChange}
          disabled={weightInputProps.disabled}
        />
      )}
    </>
  )
}

const FilenameConventionSection = ({
  getFilenameConventionProps,
}: {
  getFilenameConventionProps: () => FilenameConventionRadioGroupProps
}) => {
  const { value, disabled, ...radioGroupProps } = getFilenameConventionProps()
  const { t } = useTranslation('common', { keyPrefix: 'admin.feature_flags' })
  return (
    <>
      <Divider sx={{ mt: 2 }}>{t('export_download_filename')}</Divider>
      <RadioGroup row value={value} {...radioGroupProps}>
        {filenameConventionOptions.map((option) => (
          <FormControlLabel
            key={option.value}
            value={option.value}
            control={<Radio size="small" color="secondary" />}
            disabled={disabled}
            slotProps={{ typography: { fontSize: 14 } }}
            label={option.label}
          />
        ))}
      </RadioGroup>
      <Box typography="caption" color="text.secondary">
        {t('example', {
          example: filenameConventionOptions.find((o) => o.value === (value || ''))?.example,
        })}
      </Box>
    </>
  )
}

type FeatureFlagsSectionProps = {
  getCheckboxProps: GetCheckboxProps
  getFilenameConventionProps?: () => FilenameConventionRadioGroupProps
  getSkywardenSectionProps?: () => SkywardenSectionProps
}

export const FeatureFlagsSection = ({
  getCheckboxProps,
  getFilenameConventionProps,
  getSkywardenSectionProps,
}: FeatureFlagsSectionProps) => {
  const { t } = useTranslation('common', { keyPrefix: 'admin.feature_flags' })

  return (
    <>
      <Divider sx={{ mt: 2 }}>{t('flags')}</Divider>
      <FormControlLabel {...getCheckboxProps(featureFlagLabels.learn_v2, 'features.learn_v2')} />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.has_option_rationale, 'hasOptionRationale')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.possible_keys, 'features.possible_keys')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.rationales, 'features.rationales')}
      />
      <FormControlLabel
        {...getCheckboxProps(
          featureFlagLabels.probuilder_integration,
          'features.probuilder_integration',
        )}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.item_types_v2, 'features.item_types_v2')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.model_type_filter, 'features.model_type_filter')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.webhook_integration, 'features.webhook_integration')}
      />
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.language_selector, 'features.language_selector')}
      />
      <FormControlLabel
        {...getCheckboxProps(
          featureFlagLabels.restrict_folder_organize,
          'features.restrict_folder_organize',
        )}
      />

      <Divider sx={{ mt: 2 }}>{t('pretest_flags')}</Divider>
      <Box display="flex" justifyContent="space-between" my={1}>
        {pretestOptions.map((option) => (
          <FormControlLabel
            {...getCheckboxProps(featureFlagLabels[option.value], `features.${option.value}`)}
            key={option.value}
          />
        ))}
      </Box>

      <Divider sx={{ mt: 2 }}>{t('export_options')}</Divider>
      <Box display="flex" justifyContent="space-between" my={1}>
        {exportOptions.map((format) => (
          <FormControlLabel
            {...getCheckboxProps(format, `features.export_file.${format}`)}
            key={format}
          />
        ))}
      </Box>
      <FormControlLabel
        {...getCheckboxProps(
          featureFlagLabels.use_identifier_for_exports,
          'features.use_identifier_for_exports',
        )}
      />
      <FormControlLabel
        {...getCheckboxProps(
          featureFlagLabels.images_zip_file_export,
          'features.images_zip_file_export',
        )}
      />
      <Box typography="caption">{t('zip_export_description')}</Box>

      {getFilenameConventionProps && (
        <FilenameConventionSection getFilenameConventionProps={getFilenameConventionProps} />
      )}

      <Divider sx={{ mt: 2 }}>{t('experimental_features')}</Divider>
      <FormControlLabel
        {...getCheckboxProps(featureFlagLabels.generate_math, 'features.generate_math')}
      />

      {getSkywardenSectionProps && (
        <SkywardenSection getSkywardenSectionProps={getSkywardenSectionProps} />
      )}
    </>
  )
}
